import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AdultsPage = () => {
  const { t } = useTranslation('common');
  const [selectedCommunity, setSelectedCommunity] = useState<string | null>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const communities = {
    entrepreneurs: {
      title: t('adults_community_entrepreneurs'),
      icon: 'ri-lightbulb-line',
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600',
      description: t('adults_community_entrepreneurs_full'),
      members: t('adults_community_entrepreneurs_members'),
      topics: [
        t('adults_community_entrepreneurs_topic1'),
        t('adults_community_entrepreneurs_topic2'),
        t('adults_community_entrepreneurs_topic3'),
        t('adults_community_entrepreneurs_topic4'),
        t('adults_community_entrepreneurs_topic5')
      ],
    },
    health: {
      title: t('adults_community_health'),
      icon: 'ri-leaf-line',
      color: 'green',
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      gradientFrom: 'from-green-500',
      gradientTo: 'to-green-600',
      description: t('adults_community_health_full'),
      members: t('adults_community_health_members'),
      topics: [
        t('adults_community_health_topic1'),
        t('adults_community_health_topic2'),
        t('adults_community_health_topic3'),
        t('adults_community_health_topic4'),
        t('adults_community_health_topic5')
      ],
    },
    arts: {
      title: t('adults_community_arts'),
      icon: 'ri-palette-line',
      color: 'purple',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
      gradientFrom: 'from-purple-500',
      gradientTo: 'to-purple-600',
      description: t('adults_community_arts_full'),
      members: t('adults_community_arts_members'),
      topics: [
        t('adults_community_arts_topic1'),
        t('adults_community_arts_topic2'),
        t('adults_community_arts_topic3'),
        t('adults_community_arts_topic4'),
        t('adults_community_arts_topic5')
      ],
    },
    travel: {
      title: t('adults_community_travel'),
      icon: 'ri-plane-line',
      color: 'orange',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-600',
      gradientFrom: 'from-orange-500',
      gradientTo: 'to-orange-600',
      description: t('adults_community_travel_full'),
      members: t('adults_community_travel_members'),
      topics: [
        t('adults_community_travel_topic1'),
        t('adults_community_travel_topic2'),
        t('adults_community_travel_topic3'),
        t('adults_community_travel_topic4'),
        t('adults_community_travel_topic5')
      ],
    },
    food: {
      title: t('adults_community_food'),
      icon: 'ri-restaurant-line',
      color: 'red',
      bgColor: 'bg-red-100',
      textColor: 'text-red-600',
      gradientFrom: 'from-red-500',
      gradientTo: 'to-red-600',
      description: t('adults_community_food_full'),
      members: t('adults_community_food_members'),
      topics: [
        t('adults_community_food_topic1'),
        t('adults_community_food_topic2'),
        t('adults_community_food_topic3'),
        t('adults_community_food_topic4'),
        t('adults_community_food_topic5')
      ],
    },
    technology: {
      title: t('adults_community_technology'),
      icon: 'ri-code-line',
      color: 'teal',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-600',
      gradientFrom: 'from-teal-500',
      gradientTo: 'to-teal-600',
      description: t('adults_community_technology_full'),
      members: t('adults_community_technology_members'),
      topics: [
        t('adults_community_technology_topic1'),
        t('adults_community_technology_topic2'),
        t('adults_community_technology_topic3'),
        t('adults_community_technology_topic4'),
        t('adults_community_technology_topic5')
      ],
    },
    parenting: {
      title: t('adults_community_parenting'),
      icon: 'ri-parent-line',
      color: 'pink',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-600',
      gradientFrom: 'from-pink-500',
      gradientTo: 'to-pink-600',
      description: t('adults_community_parenting_full'),
      members: t('adults_community_parenting_members'),
      topics: [
        t('adults_community_parenting_topic1'),
        t('adults_community_parenting_topic2'),
        t('adults_community_parenting_topic3'),
        t('adults_community_parenting_topic4'),
        t('adults_community_parenting_topic5')
      ],
    },
    education: {
      title: t('adults_community_education'),
      icon: 'ri-book-line',
      color: 'yellow',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600',
      gradientFrom: 'from-yellow-500',
      gradientTo: 'to-yellow-600',
      description: t('adults_community_education_full'),
      members: t('adults_community_education_members'),
      topics: [
        t('adults_community_education_topic1'),
        t('adults_community_education_topic2'),
        t('adults_community_education_topic3'),
        t('adults_community_education_topic4'),
        t('adults_community_education_topic5')
      ],
    },
  };

  const CommunityModal = ({ communityKey }: { communityKey: string }) => {
    const community = communities[communityKey as keyof typeof communities];
    if (!community) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
        onClick={() => setSelectedCommunity(null)}
      >
        <div 
          className="bg-gray-900 border border-gray-800 rounded-3xl max-w-2xl w-full p-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setSelectedCommunity(null)}
            className="absolute top-6 right-6 w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-300"></i>
          </button>

          <div className={`w-20 h-20 ${community.bgColor} rounded-full flex items-center justify-center mb-6`}>
            <i className={`${community.icon} text-3xl ${community.textColor}`}></i>
          </div>

          <h2 className="text-3xl font-bold mb-4 text-white">{community.title}</h2>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex items-center space-x-2">
              <i className="ri-user-line text-gray-400"></i>
              <span className="text-gray-300 font-semibold">{community.members}</span>
            </div>
            <div className={`px-3 py-1 ${community.bgColor} ${community.textColor} rounded-full text-sm font-semibold`}>
              {t('adults_community_active')}
            </div>
          </div>

          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {community.description}
          </p>

          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 text-white">{t('adults_community_popular_topics')}</h3>
            <div className="flex flex-wrap gap-2">
              {community.topics.map((topic, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-full text-sm font-medium"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>

          <button
            className={`w-full bg-gradient-to-r ${community.gradientFrom} ${community.gradientTo} text-white py-4 rounded-xl font-bold text-lg hover:scale-102 transition-all shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2`}
          >
            <i className="ri-user-add-line"></i>
            <span>{t('adults_community_join_btn')}</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black font-['Inter']">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 transition-all duration-300 backdrop-blur-md bg-black/90 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
                alt="FaceTrue Logo" 
                className="w-10 h-10 rounded-full"
              />
              <span className="text-xl font-bold text-white">FaceTrue Adults</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm font-medium cursor-pointer">
                {t('adults_nav_features')}
              </button>
              <button onClick={() => scrollToSection('community')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm font-medium cursor-pointer">
                {t('adults_nav_community')}
              </button>
              <button onClick={() => scrollToSection('benefits')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm font-medium cursor-pointer">
                {t('adults_nav_benefits')}
              </button>
              <a 
                href="/video-adults"
                className="bg-gradient-to-r from-cyan-500 to-teal-500 text-black px-5 py-2 rounded-full font-bold hover:scale-105 transition-all whitespace-nowrap cursor-pointer no-underline flex items-center space-x-2 shadow-lg"
              >
                <i className="ri-video-line"></i>
                <span>🎬 VIDEO</span>
              </a>
            </div>
            <a 
              href="/"
              className="bg-cyan-500 text-black px-6 py-2 rounded-full font-semibold hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('adults_back_home')}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/59d57724c158d0db96c45acf154fbb72.jpg"
            alt="Adults Background"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="w-24 h-24 bg-cyan-500 rounded-full flex items-center justify-center mb-8 mx-auto shadow-2xl">
            <i className="ri-user-3-line text-5xl text-black"></i>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight text-white">
            {t('adults_hero_title')}<br />
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
              {t('adults_hero_subtitle')}
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: t('adults_hero_desc') }} />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('adults_features_title')}</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('adults_features_subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-briefcase-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('adults_feature1_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('adults_feature1_desc')}
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:shadow-2xl hover:shadow-teal-500/10 transition-all duration-500">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('adults_feature2_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('adults_feature2_desc')}
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-chat-3-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('adults_feature3_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('adults_feature3_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('adults_community_title')}</h2>
            <p className="text-xl text-gray-400">
              {t('adults_community_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('entrepreneurs');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-cyan-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-2xl text-cyan-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_entrepreneurs')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_entrepreneurs_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('health');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-green-400 hover:shadow-xl hover:shadow-green-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-leaf-line text-2xl text-green-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_health')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_health_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('arts');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-fuchsia-400 hover:shadow-xl hover:shadow-fuchsia-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-fuchsia-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-palette-line text-2xl text-fuchsia-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_arts')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_arts_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('travel');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-orange-400 hover:shadow-xl hover:shadow-orange-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-orange-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-plane-line text-2xl text-orange-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_travel')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_travel_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('food');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-red-400 hover:shadow-xl hover:shadow-red-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-restaurant-line text-2xl text-red-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_food')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_food_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('technology');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-teal-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-code-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_technology')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_technology_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('parenting');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-pink-400 hover:shadow-xl hover:shadow-pink-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-pink-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-parent-line text-2xl text-pink-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_parenting')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_parenting_desc')}</p>
            </a>

            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCommunity('education');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-yellow-400 hover:shadow-xl hover:shadow-yellow-500/10 transition-all cursor-pointer text-left w-full block no-underline"
            >
              <div className="w-14 h-14 bg-yellow-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-line text-2xl text-yellow-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('adults_community_education')}</h3>
              <p className="text-sm text-gray-400">{t('adults_community_education_desc')}</p>
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('adults_benefits_title')}</h2>
            <p className="text-xl text-gray-400">
              {t('adults_benefits_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('adults_benefit1_title')}</h3>
                  <p className="text-gray-400">{t('adults_benefit1_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-delete-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('adults_benefit2_title')}</h3>
                  <p className="text-gray-400">{t('adults_benefit2_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-fuchsia-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('adults_benefit3_title')}</h3>
                  <p className="text-gray-400">{t('adults_benefit3_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('adults_benefit4_title')}</h3>
                  <p className="text-gray-400">{t('adults_benefit4_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Feed Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            <i className="ri-newspaper-line mr-3"></i>
            Community Feed
          </h2>

          {/* Post Creation */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6 shadow-lg">
            <div className="flex items-center space-x-4 mb-4">
              <img 
                src="https://readdy.ai/api/search-image?query=professional-business-person-avatar-portrait-with-friendly-smile-and-confident-expression-in-modern-office-setting-with-clean-simple-background-high-quality-headshot-photography&width=100&height=100&seq=adults-avatar-1&orientation=squarish"
                alt="User"
                className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500"
              />
              <input
                type="text"
                placeholder="What's on your mind?"
                className="flex-1 bg-gray-800 rounded-full px-6 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex space-x-4">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-image-add-line text-xl text-green-400"></i>
                  <span className="text-gray-300 font-medium">Photo/Video</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-emotion-happy-line text-xl text-cyan-400"></i>
                  <span className="text-gray-300 font-medium">Feeling</span>
                </button>
              </div>
              <button className="bg-gradient-to-r from-cyan-500 to-teal-500 text-black px-6 py-2 rounded-lg font-bold hover:scale-105 transition-all shadow-md cursor-pointer whitespace-nowrap">
                Post
              </button>
            </div>
          </div>

          {/* News Feed Posts */}
          <div className="space-y-6">
            {/* Post 1 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional-business-woman-avatar-portrait-with-warm-smile-wearing-business-attire-in-modern-office-with-clean-simple-background-high-quality-headshot&width=100&height=100&seq=adults-user-1&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500"
                  />
                  <div>
                    <h3 className="font-bold text-white">Sarah Johnson</h3>
                    <p className="text-sm text-gray-400">2 hours ago</p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-300 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Had an amazing leadership workshop today! Thank you to all colleagues who participated enthusiastically. 💼✨
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=modern-professional-business-workshop-training-session-with-diverse-group-of-adults-in-bright-conference-room-with-presentation-screen-and-collaborative-atmosphere-clean-simple-background&width=800&height=500&seq=adults-post-1&orientation=landscape"
                alt="Post"
                className="w-full h-80 object-cover rounded-xl mb-4"
              />
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-800 mb-3">
                <span className="text-gray-300 font-medium">❤️ 234 likes</span>
                <span className="text-transparent bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text font-bold">RESPECT 18</span>
                <span className="text-gray-300 font-medium">💬 56 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-400"></i>
                  <span className="text-white font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-cyan-400"></i>
                  <span className="text-white font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-400"></i>
                  <span className="text-white font-semibold">Share</span>
                </button>
              </div>
            </div>

            {/* Post 2 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional-business-man-avatar-portrait-with-confident-smile-wearing-suit-in-modern-office-with-clean-simple-background-high-quality-headshot-photography&width=100&height=100&seq=adults-user-2&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500"
                  />
                  <div>
                    <h3 className="font-bold text-white">Michael Chen</h3>
                    <p className="text-sm text-gray-400">5 hours ago</p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-300 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Weekend hiking with family. Fresh air and beautiful scenery! 🏔️🌲
              </p>
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-800 mb-3">
                <span className="text-gray-300 font-medium">❤️ 189 likes</span>
                <span className="text-transparent bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text font-bold">RESPECT 18</span>
                <span className="text-gray-300 font-medium">💬 42 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-400"></i>
                  <span className="text-white font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-cyan-400"></i>
                  <span className="text-white font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-400"></i>
                  <span className="text-white font-semibold">Share</span>
                </button>
              </div>
            </div>

            {/* Post 3 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional-business-woman-avatar-portrait-with-friendly-expression-wearing-professional-attire-in-modern-workspace-with-clean-simple-background-high-quality-headshot&width=100&height=100&seq=adults-user-3&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500"
                  />
                  <div>
                    <h3 className="font-bold text-white">Emily Rodriguez</h3>
                    <p className="text-sm text-gray-400">1 day ago</p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-300 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Just completed an online Digital Marketing course. So much new and useful knowledge! 📚💡
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=modern-digital-marketing-online-learning-workspace-with-laptop-notebook-and-coffee-on-clean-desk-in-bright-home-office-with-plants-and-simple-background-professional-study-environment&width=800&height=500&seq=adults-post-3&orientation=landscape"
                alt="Post"
                className="w-full h-80 object-cover rounded-xl mb-4"
              />
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-800 mb-3">
                <span className="text-gray-300 font-medium">❤️ 312 likes</span>
                <span className="text-transparent bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text font-bold">RESPECT 18</span>
                <span className="text-gray-300 font-medium">💬 78 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-400"></i>
                  <span className="text-white font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-cyan-400"></i>
                  <span className="text-white font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-400"></i>
                  <span className="text-white font-semibold">Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img 
            src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
            alt="FaceTrue Logo" 
            className="w-8 h-8 mx-auto mb-4 rounded-full"
          />
          <p className="text-gray-400 text-sm mb-2">
            {t('adults_footer_copyright')}
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {t('adults_footer_desc')}
          </p>
          <a 
            href="https://readdy.ai/?origin=logo" 
            className="text-gray-600 text-xs hover:text-gray-400 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>

      {/* Community Modal */}
      {selectedCommunity && <CommunityModal communityKey={selectedCommunity} />}
    </div>
  );
};

export default AdultsPage;